// Aufgeteilt aus dem früheren 2600-Zeilen-Monolithen `musicXmlDom.ts` (Import-Pfad unverändert:
// `@/composables/musicXmlDom` zeigt auf `index.ts`). Abhängigkeitsregel im Ordner: geteilte
// Basis-Helfer liegen in `core.ts`; gerichtete Geschwister-Imports sind erlaubt, ZYKLEN nicht
// (aktuell: bass→chordInference, notes→core, diatonic→notes, structure→notes,
// lyrics→bass, clipboard→structure, fragment→structure).
import {HANDWRITTEN_BASS_COLOR, INFERRED_CHORD_COLOR, isInferredColor} from './chordInference';
import {type LyricColorTarget, type NoteInfo, chordGroupOf, findMeasureInPart, findMeasureInPartByIndex, findPartAndMeasureNote, findPartForNote, getVoiceNotes, parseMusicXml, serializeMusicXml, voiceOf} from './core';

// ============ Bass-Vorschau-Part ============

/**
 * `<part-name>` des klingenden Bass-Vorschau-Parts – Spiegel von
 * `griffschrift/mapping/bass_preview.py::BASS_PREVIEW_PART_NAME`.
 */
export const BASS_PREVIEW_PART_NAME = 'Harmonika-Bass';

/**
 * `none` = kein Bass-System · `generated` = vollständig maschinell erzeugt ·
 * `edited` = mindestens ein Takt vom Nutzer angefasst (wird beim Neuerzeugen verschont).
 */
export type BassPreviewState = 'none' | 'generated' | 'edited';

/**
 * Der `<part>` des Bass-Vorschau-Systems, oder `null`.
 *
 * Aufgelöst über den `<part-name>` im `<part-list>` und dessen `id` – nicht über die Position:
 * der Vorschau-Part kann je nach Datei an beliebiger Stelle stehen.
 */
export function findBassPreviewPart(doc: Document): Element | null {
    for (const scorePart of doc.querySelectorAll('part-list score-part')) {
        if (scorePart.querySelector('part-name')?.textContent?.trim() !== BASS_PREVIEW_PART_NAME) continue;
        const id = scorePart.getAttribute('id');
        if (!id) continue;
        return [...doc.querySelectorAll('part')].find((part) => part.getAttribute('id') === id) ?? null;
    }
    return null;
}

/**
 * Leitet den Anzeigezustand des Bass-Systems **aus dem Inhalt** ab (überlebt Reload und Undo –
 * dasselbe Prinzip wie {@link chordInferenceStatus}).
 *
 * `edited`, sobald eine klingende Note den `INFERRED_CHORD_COLOR`-Marker verloren hat. Pausen
 * zählen nicht mit: die ehrliche Ganztaktpause bei unbekanntem Akkord trägt nie eine Farbe.
 */
export function bassPreviewStatus(musicXml: string): BassPreviewState {
    if (!musicXml || !musicXml.includes(BASS_PREVIEW_PART_NAME)) return 'none';
    const part = findBassPreviewPart(parseMusicXml(musicXml));
    if (!part) return 'none';

    for (const noteEl of part.querySelectorAll('note')) {
        if (noteEl.querySelector('rest')) continue;
        if (!isInferredColor(noteEl.getAttribute('color'))) return 'edited';
    }
    return 'generated';
}

/** Spielweise eines Takts im Bass-Vorschau-System (Spiegel von `mapping/bass_preview.py`). */
export const BASS_STYLE_WECHSELBASS = 'wechselbass';
export const BASS_STYLE_AKKORDTOENE = 'akkordtoene';
export const BASS_RUN_PREFIX = 'lauf:';

/** Die im Repertoire fest eingebauten Helikon-Bassläufe (Spiegel von `HELIKON_RUNS`). */
export const BASS_RUN_LABELS: Readonly<Record<string, string>> = {
    abwaerts: 'Abwärts (a–g–e–c)',
    aufwaerts: 'Aufwärts (c–e–g–a)',
    durchgang: 'Durchgang zur Quinte',
    dreiklang: 'Dreiklang über die Oktave',
};

const BASS_STYLES_META_FIELD = 'BassPreviewStyles';

/**
 * Die vermerkten Spielweisen je **Takt-Index** (0-basiert), aus den Score-Metadaten.
 *
 * Gelesen, nicht geschrieben: der Vermerk entsteht serverseitig beim Erzeugen des Bass-Systems
 * (`POST /api/bass-part`), damit es genau **einen** Schreibpfad gibt. Ein ungültiger Inhalt (alte
 * oder fremde Datei) liefert `{}` statt zu werfen.
 */
export function readBassPreviewStyles(musicXml: string): Record<number, string> {
    if (!musicXml || !musicXml.includes(BASS_STYLES_META_FIELD)) return {};
    const field = parseMusicXml(musicXml)
        .querySelector(`miscellaneous-field[name="${BASS_STYLES_META_FIELD}"]`);
    if (!field?.textContent) return {};

    try {
        const parsed = JSON.parse(field.textContent) as unknown;
        if (!parsed || typeof parsed !== 'object') return {};
        const styles: Record<number, string> = {};
        for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
            const index = Number(key);
            if (Number.isInteger(index) && typeof value === 'string' && value) styles[index] = value;
        }
        return styles;
    } catch {
        return {};
    }
}

/**
 * Die globalen Staff-Indizes des Bass-Vorschau-Parts (leer, wenn es keinen gibt).
 *
 * Gezählt wie in {@link findPartForNote} über die `<staves>` der vorangehenden Parts – dieselbe
 * Kette, die auch die Notenadressierung nutzt. Konsument ist die Rot-Markierung unspielbarer
 * Noten: sie vergleicht Notennamen über **alle** Notenzeilen und würde sonst Töne im Bass-System
 * einfärben, die dort völlig in Ordnung sind (die Griffschrift-Ausgabe ist ohnehin bassfrei).
 */
export function bassPreviewStaffIndices(musicXml: string): Set<number> {
    const out = new Set<number>();
    if (!musicXml || !musicXml.includes(BASS_PREVIEW_PART_NAME)) return out;

    const doc = parseMusicXml(musicXml);
    const bassPart = findBassPreviewPart(doc);
    if (!bassPart) return out;

    let acc = 0;
    for (const part of doc.querySelectorAll('part')) {
        const staves = parseInt(part.querySelector('staves')?.textContent || '1', 10) || 1;
        if (part === bassPart) {
            for (let i = 0; i < staves; i++) out.add(acc + i);
            break;
        }
        acc += staves;
    }
    return out;
}

/**
 * Markiert alle Takte, in denen ``notes`` liegen, als **Handarbeit** – aber **nur** im
 * Bass-Vorschau-Part. Zweierlei passiert dabei:
 *
 * 1. Jede klingende `<note>` (samt `<notehead>`) bekommt `HANDWRITTEN_BASS_COLOR` anstelle des
 *    grünen „maschinell erzeugt“-Markers. Damit lässt die Engine den Takt beim Neuerzeugen
 *    stehen (sie erkennt Handarbeit am *Fehlen* des Markers) – **und** man sieht im Notenbild,
 *    welche Takte einem gehören: genau die bestimmen die Bass-Zeile der Griffschrift.
 *    Nötig, weil die Edit-Mutationen ein vorhandenes `color` unangetastet lassen (eine reine
 *    Tonhöhenänderung bliebe sonst „erzeugt“) und frisch eingefügte Noten gar keines haben.
 * 2. Die Bass-Silbe (`<lyric number="3">`) fällt weg. Sie ist maschinelle Ableitung aus der
 *    **alten** Tonhöhe und wäre nach der Bearbeitung schlicht falsch – eine ehrliche Lücke ist
 *    besser als eine Falschaussage über das, was gedruckt wird. Der nächste Server-Schritt
 *    („Neu erzeugen“, Spielweise-Wahl) leitet sie aus den geschriebenen Noten neu ab, mit
 *    denselben Funktionen wie die Konvertierung.
 *
 * **Pausen bleiben unberührt**, wie überall in dieser Kette: `bassPreviewStatus` und der
 * Regenerier-Schutz der Engine zählen sie nicht mit – eine dunkelblaue Pause behauptete
 * einen Besitz, den die Engine nicht einlöst.
 *
 * **Granularität ist der Takt**, passend zur Granularität des Neuerzeugens und des Overrides.
 * Gibt das XML unverändert zurück, wenn kein Bass-System existiert oder keine Note darin liegt.
 */
export function markBassPreviewMeasuresAsEdited(musicXml: string, notes: NoteInfo[]): string {
    if (!musicXml || !notes.length || !musicXml.includes(BASS_PREVIEW_PART_NAME)) return musicXml;

    const doc = parseMusicXml(musicXml);
    const bassPart = findBassPreviewPart(doc);
    if (!bassPart) return musicXml;

    let touched = false;
    for (const info of notes) {
        if (findPartForNote(doc, info.staffIndex) !== bassPart) continue;
        const measure = info.measureIndex === undefined
            ? findMeasureInPart(bassPart, info.measureNumber)
            : findMeasureInPartByIndex(bassPart, info.measureIndex);
        if (!measure) continue;
        for (const noteEl of measure.querySelectorAll('note')) {
            if (noteEl.querySelector('rest')) continue;
            for (const element of [noteEl, ...noteEl.querySelectorAll('notehead')]) {
                if (element.getAttribute('color') === HANDWRITTEN_BASS_COLOR) continue;
                element.setAttribute('color', HANDWRITTEN_BASS_COLOR);
                touched = true;
            }
            const lyric = bassLyricOf(noteEl);
            if (lyric) {
                lyric.remove();
                touched = true;
            }
        }
    }
    return touched ? serializeMusicXml(doc) : musicXml;
}

// ============ Bass-Notation (Bass-Lyric der Griffschrift-Ausgabe) ============

/** Spiegel von `BASS_LYRIC_NUMBER`/`BASS_LYRIC_IDENTIFIER`/`BASS_LYRIC_RELATIVE_Y` in
 *  `griffschrift/mapping/bass.py` – so sehen vom Frontend angelegte Bass-Lyrics exakt wie die des
 *  Konverters aus (und `remove_existing_bass_annotations` erkennt sie bei einer Neukonvertierung). */
export const BASS_LYRIC_NUMBER = '3';
export const BASS_LYRIC_NAME = 'Bass';
const BASS_LYRIC_COLOR = '#000000';
const BASS_LYRIC_RELATIVE_Y = '-22';

/** Die Bass-Lyric einer `<note>` (`number="3"` ODER `name="Bass"` – dieselbe Erkennung wie die
 *  Engine), sonst `null`. Der Druck-Unterstrich (`number="10"`) und Liedtext (`1..n`) zählen nicht. */
export function bassLyricOf(noteEl: Element): Element | null {
    // querySelectorAll statt tagName-Vergleich: wie `setDruckUnderline` – robust gegenüber der
    // Tag-Schreibweise frisch erzeugter Elemente (happy-dom liefert dort Großbuchstaben).
    return [...noteEl.querySelectorAll('lyric')].find((l) =>
        l.getAttribute('number') === BASS_LYRIC_NUMBER || l.getAttribute('name') === BASS_LYRIC_NAME,
    ) ?? null;
}

/** Bass-Text der adressierten Note/Pause/Träger-Note (Akkordton → Primärnote der Gruppe); `null`
 *  ohne Bass-Lyric oder bei unauflösbarer Adresse. */
export function readBassLyricText(musicXml: string, noteInfo: NoteInfo): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const found = findPartAndMeasureNote(doc, noteInfo);
        if (!found) return null;
        const primary = chordGroupOf(found.note)[0] ?? found.note;
        return bassLyricOf(primary)?.querySelector('text')?.textContent ?? null;
    } catch (e) {
        console.error('Error reading bass lyric from MusicXML:', e);
        return null;
    }
}

/** Setzt (`text`) bzw. entfernt (`''`) die Bass-Lyric EINER `<note>`. Neue Lyrics bekommen die
 *  Konverter-Attribute und stehen als letztes Kind (`<lyric>` ist schema-mäßig zuletzt). */
export function setBassLyric(doc: Document, noteEl: Element, text: string): void {
    const existing = bassLyricOf(noteEl);
    if (!text) {
        existing?.remove();
        return;
    }
    if (existing) {
        // Explizit `Element`: TS typisiert querySelector('text') als SVGTextElement.
        let textEl: Element | null = existing.querySelector('text');
        if (!textEl) {
            textEl = doc.createElement('text');
            existing.appendChild(textEl);
        }
        textEl.textContent = text;
        return;
    }
    const lyric = doc.createElement('lyric');
    lyric.setAttribute('number', BASS_LYRIC_NUMBER);
    lyric.setAttribute('name', BASS_LYRIC_NAME);
    lyric.setAttribute('color', BASS_LYRIC_COLOR);
    lyric.setAttribute('placement', 'below');
    lyric.setAttribute('relative-y', BASS_LYRIC_RELATIVE_Y);
    const syllabic = doc.createElement('syllabic');
    syllabic.textContent = 'single';
    const textEl = doc.createElement('text');
    textEl.textContent = text;
    lyric.appendChild(syllabic);
    lyric.appendChild(textEl);
    noteEl.appendChild(lyric);
}

/**
 * Setzt oder entfernt **client-seitig** die Bass-Notation (Bass-Lyric) aller adressierten Elemente der
 * Griffschrift-**Ausgabe** – Noten, Pausen und unsichtbare Bass-Träger (Stimme 2) gleichermaßen.
 * `text` wird getrimmt; leer/`null` = Lyric entfernen. Akkordgruppen werden über ihre Primärnote
 * beschrieben (dedupliziert, auch wenn mehrere Akkordtöne ausgewählt sind). Nur `<lyric>` ändert sich –
 * **nie `<duration>`** –, daher bleibt die Taktlänge gültig. `null` nur, wenn **keine** Adresse
 * auflösbar ist (z. B. Alt-Inhalte mit nicht-numerischen `<voice>`-IDs).
 */
export function setBassLyricInMusicXml(
    musicXml: string,
    infos: readonly NoteInfo[],
    text: string | null,
): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const value = (text ?? '').trim();
        const targets = new Set<Element>();
        for (const info of infos) {
            const found = findPartAndMeasureNote(doc, info);
            if (!found) continue;
            targets.add(chordGroupOf(found.note)[0] ?? found.note);
        }
        if (!targets.size) return null;
        for (const noteEl of targets) setBassLyric(doc, noteEl, value);
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error setting bass lyric in MusicXML:', e);
        return null;
    }
}


/**
 * Die Bass-Silben, die aus **übernommener Handarbeit** stammen – als Adressen für die
 * SVG-Nachfärbung (`composables/osmd/useScoreLabelColors.ts`). Leeres/ungültiges XML → `[]`.
 *
 * Nötig, weil OSMD `<lyric color>` **nicht** liest (es wertet `color` nur an `<stem>`,
 * `<notehead>`, `<note>` und Expression-`<words>` aus): die Engine schreibt
 * `HANDWRITTEN_BASS_COLOR` zwar in die Ausgabe, am Bildschirm bliebe die Silbe ohne diesen
 * Nachlauf schwarz. Im MusicXML-Export (MuseScore/PDF) ist sie dagegen von Haus aus farbig.
 *
 * **Notengenau, nicht taktweise** – anders als bei den Akkordsymbolen: `setBassLyric` kann einer
 * einzelnen Note nachträglich eine schwarze Silbe zwischen dunkelblaue setzen.
 *
 * **Gematcht wird nur auf die Handarbeit-Farbe, nie auf „schwarz“.** Im Bestand kommen dafür zwei
 * Schreibweisen vor – `"black"` aus der Engine (`bass.py::BASS_LYRIC_COLOR`) und `#000000` aus
 * diesem Frontend; alles andere bleibt schlicht beim Default.
 */
export function handwrittenBassLyricTargets(musicXml: string): LyricColorTarget[] {
    if (!musicXml) return [];
    try {
        const doc = parseMusicXml(musicXml);
        const out: LyricColorTarget[] = [];
        const wanted = HANDWRITTEN_BASS_COLOR.toLowerCase();
        let staffAcc = 0;
        for (const part of doc.querySelectorAll('part')) {
            const staves = parseInt(part.querySelector('staves')?.textContent || '1', 10) || 1;
            [...part.querySelectorAll('measure')].forEach((measure, measureIndex) => {
                const voices = new Set([...measure.querySelectorAll('note')].map(voiceOf));
                for (const voice of voices) {
                    getVoiceNotes(measure, voice).forEach((noteEl, voiceNoteIndex) => {
                        if (bassLyricOf(noteEl)?.getAttribute('color')?.toLowerCase() !== wanted) return;
                        const staff = parseInt(noteEl.querySelector('staff')?.textContent || '1', 10) || 1;
                        out.push({
                            staffIndex: staffAcc + staff - 1,
                            measureIndex,
                            voice,
                            voiceNoteIndex,
                            color: HANDWRITTEN_BASS_COLOR,
                        });
                    });
                }
            });
            staffAcc += staves;
        }
        return out;
    } catch (e) {
        console.error('Error collecting handwritten bass lyric targets:', e);
        return [];
    }
}
